import { useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import intakeHero from "@/assets/intake-hero.jpg";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle } from "lucide-react";

const intakeSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100),
  lastName: z.string().trim().min(1, "Last name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().min(1, "Phone number is required").max(30),
  company: z.string().trim().max(200).optional(),
  referralSource: z.string().trim().max(200).optional(),
  caseType: z.string().min(1, "Please select a case type"),
  description: z.string().trim().min(10, "Please provide at least a brief description").max(5000),
  priorAttorney: z.enum(["yes", "no", "unsure"]),
  priorAttorneyVM: z.enum(["yes", "no"]).optional(),
  priorAttorneyVMName: z.string().trim().max(200).optional(),
  preferredContact: z.enum(["email", "phone", "either"]),
  bestTimeToReach: z.enum(["morning", "afternoon", "evening", "anytime"]),
  communicationConsent: z.boolean().refine(val => val === true, { message: "You must consent to communications to submit this form" }),
  honeypot: z.string().max(0).optional(),
  confirmHuman: z.boolean().refine(val => val === true, { message: "Please confirm you are not a robot" }),
});

type IntakeFormValues = z.infer<typeof intakeSchema>;

const caseTypes = [
  "Admiralty & Maritime",
  "Aviation",
  "Business & Commercial Litigation",
  "Construction",
  "Employment & Labor",
  "Insurance Coverage & Bad Faith",
  "Product Liability",
  "Professional Liability",
  "Real Estate",
  "Transportation",
  "Other",
];

const IntakePage = () => {
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const form = useForm<IntakeFormValues>({
    resolver: zodResolver(intakeSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      referralSource: "",
      caseType: "",
      description: "",
      priorAttorney: "no",
      priorAttorneyVM: undefined,
      priorAttorneyVMName: "",
      preferredContact: "either",
      bestTimeToReach: "anytime",
      communicationConsent: false,
      honeypot: "",
      confirmHuman: false,
    },
  });

  const priorAttorney = form.watch("priorAttorney");
  const priorAttorneyVM = form.watch("priorAttorneyVM");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: IntakeFormValues) => {
    // Honeypot check — if filled, silently reject
    if (data.honeypot) {
      setSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("send-intake-email", {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          referralSource: data.referralSource,
          caseType: data.caseType,
          description: data.description,
          priorAttorney: data.priorAttorney,
          priorAttorneyVM: data.priorAttorneyVM,
          priorAttorneyVMName: data.priorAttorneyVMName,
          preferredContact: data.preferredContact,
          bestTimeToReach: data.bestTimeToReach,
        },
      });

      if (error) throw error;

      setSubmitted(true);
      toast({ title: "Form submitted", description: "Your intake form has been sent successfully. Our team will be in touch shortly." });
    } catch (error) {
      console.error("Intake submission error:", error);
      toast({
        title: "Submission failed",
        description: "There was an error sending your form. Please try again or contact us directly at intake@vaughanmclean.com.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <PageHero backgroundImage={intakeHero} title="Client Intake" subtitle="Get Started" />
        <section className="section-dark py-24">
          <div className="container mx-auto px-8 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto text-center"
            >
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="font-heading text-4xl font-light text-foreground mb-4">
                Thank You
              </h2>
              <p className="text-muted-foreground font-body leading-relaxed mb-8">
                Your intake form has been submitted successfully. A member of our team will review your information and be in touch shortly.
              </p>
              <Button onClick={() => setSubmitted(false)} variant="outline">
                Submit Another Inquiry
              </Button>
            </motion.div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="Client Intake | Vaughan McLean" description="Submit a client intake form to get started with Vaughan McLean. Our attorneys will review your matter and reach out promptly." />
      <PageHero backgroundImage={intakeHero} title="Client Intake" subtitle="Get Started" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-muted-foreground font-body leading-relaxed mb-12">
                Please complete the form below to help us understand your legal needs. All information provided is kept strictly confidential. Submitting this form does not establish an attorney-client relationship.
              </p>
            </motion.div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
                {/* Contact Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="font-heading text-2xl font-light text-foreground mb-6 pb-2 border-b border-border">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">First Name *</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-secondary border-border text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">Last Name *</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-secondary border-border text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">Email Address *</FormLabel>
                          <FormControl>
                            <Input type="email" {...field} className="bg-secondary border-border text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">Phone Number *</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} className="bg-secondary border-border text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">Company / Organization</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-secondary border-border text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="referralSource"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">How did you hear about us?</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-secondary border-border text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </motion.div>

                {/* Case Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <h3 className="font-heading text-2xl font-light text-foreground mb-6 pb-2 border-b border-border">
                    Case Information
                  </h3>
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="caseType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">Area of Law *</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              className="flex h-10 w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <option value="" className="bg-secondary">Select an area...</option>
                              {caseTypes.map((type) => (
                                <option key={type} value={type} className="bg-secondary">
                                  {type}
                                </option>
                              ))}
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">Brief Description of Your Matter *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              rows={6}
                              placeholder="Please describe your legal matter, including any relevant dates, parties involved, and the outcome you are seeking."
                              className="bg-secondary border-border text-foreground resize-none"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="priorAttorney"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground font-body text-sm">
                            Have you previously consulted with an attorney about this matter?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="flex gap-6 mt-2"
                            >
                              {[
                                { value: "yes", label: "Yes" },
                                { value: "no", label: "No" },
                                { value: "unsure", label: "Unsure" },
                              ].map((option) => (
                                <div key={option.value} className="flex items-center space-x-2">
                                  <RadioGroupItem value={option.value} id={`prior-${option.value}`} />
                                  <Label htmlFor={`prior-${option.value}`} className="text-muted-foreground font-body text-sm cursor-pointer">
                                    {option.label}
                                  </Label>
                                </div>
                              ))}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {priorAttorney === "yes" && (
                      <div className="space-y-6 pl-4 border-l-2 border-primary/30 mt-4">
                        <FormField
                          control={form.control}
                          name="priorAttorneyVM"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-muted-foreground font-body text-sm">
                                Was the attorney from Vaughan McLean?
                              </FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex gap-6 mt-2"
                                >
                                  {[
                                    { value: "yes", label: "Yes" },
                                    { value: "no", label: "No" },
                                  ].map((option) => (
                                    <div key={option.value} className="flex items-center space-x-2">
                                      <RadioGroupItem value={option.value} id={`vm-${option.value}`} />
                                      <Label htmlFor={`vm-${option.value}`} className="text-muted-foreground font-body text-sm cursor-pointer">
                                        {option.label}
                                      </Label>
                                    </div>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {priorAttorneyVM === "yes" && (
                          <FormField
                            control={form.control}
                            name="priorAttorneyVMName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-muted-foreground font-body text-sm">
                                  Which attorney did you speak with?
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Attorney name" className="bg-secondary border-border text-foreground" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Preferred Contact */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <h3 className="font-heading text-2xl font-light text-foreground mb-6 pb-2 border-b border-border">
                    Communication Preference
                  </h3>
                  <FormField
                    control={form.control}
                    name="preferredContact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-muted-foreground font-body text-sm">
                          How would you prefer to be contacted?
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex gap-6 mt-2"
                          >
                            {[
                              { value: "email", label: "Email" },
                              { value: "phone", label: "Phone" },
                              { value: "either", label: "Either" },
                            ].map((option) => (
                              <div key={option.value} className="flex items-center space-x-2">
                                <RadioGroupItem value={option.value} id={`contact-${option.value}`} />
                                <Label htmlFor={`contact-${option.value}`} className="text-muted-foreground font-body text-sm cursor-pointer">
                                  {option.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="bestTimeToReach"
                    render={({ field }) => (
                      <FormItem className="mt-6">
                        <FormLabel className="text-muted-foreground font-body text-sm">
                          Best time to be reached?
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-wrap gap-6 mt-2"
                          >
                            {[
                              { value: "morning", label: "Morning" },
                              { value: "afternoon", label: "Afternoon" },
                              { value: "evening", label: "Evening" },
                              { value: "anytime", label: "Anytime" },
                            ].map((option) => (
                              <div key={option.value} className="flex items-center space-x-2">
                                <RadioGroupItem value={option.value} id={`time-${option.value}`} />
                                <Label htmlFor={`time-${option.value}`} className="text-muted-foreground font-body text-sm cursor-pointer">
                                  {option.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>

                {/* Consent & Verification */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.35 }}
                  viewport={{ once: true }}
                >
                  <h3 className="font-heading text-2xl font-light text-foreground mb-6 pb-2 border-b border-border">
                    Consent & Verification
                  </h3>

                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="communicationConsent"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-muted-foreground font-body text-sm cursor-pointer">
                              I consent to communications *
                            </FormLabel>
                            <FormDescription className="text-muted-foreground/70 font-body text-xs leading-relaxed">
                              By checking this box, I expressly consent to receive communications from Vaughan McLean, PLLC, including via email, telephone, and text message, at the contact information provided above, regarding my legal inquiry and related matters. I understand that I may revoke this consent at any time by contacting the firm in writing. This consent is not a condition of retaining legal services.
                            </FormDescription>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* Honeypot — hidden from real users */}
                    <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                      <FormField
                        control={form.control}
                        name="honeypot"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Leave this blank</FormLabel>
                            <FormControl>
                              <Input {...field} tabIndex={-1} autoComplete="off" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="confirmHuman"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-muted-foreground font-body text-sm cursor-pointer">
                              I am not a robot *
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                </motion.div>

                {/* Disclaimer & Submit */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                  className="pt-4"
                >
                  <p className="text-muted-foreground font-body text-xs leading-relaxed mb-8">
                    By submitting this form, you acknowledge that the information provided is accurate to the best of your knowledge. Submission of this form does not create an attorney-client relationship. Confidential or time-sensitive information should not be submitted through this form.
                  </p>
                  <Button type="submit" size="lg" className="font-body tracking-wider uppercase text-sm" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit Intake Form"}
                  </Button>
                </motion.div>
              </form>
            </Form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IntakePage;
